import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useTravelRequestFilters } from '@/module/Travel/Registry/useTravelRequestFilters';
import { TravelRequestCard } from '@/module/Travel/Request/components/Card/TravelRequestCard';
import { TravelOverlapCardFrame } from '@/module/Travel/Request/components/TravelOverlapButton';
import { isTravelApproverRole, isTravelOfficeRole } from '@/module/Travel/travelRoles';

import { BooleanFilter, Browse, NoContentPlaceholder, useBooleanFilter, usePagination } from '@/core/components';

import { RoleType, useCurrentUser } from '@feature/security';
import { FilterBox, SearchFilter, useSearch, useSortDirection } from '@feature/utils';

import { TravelDeanMailRouting } from './components/TravelDeanMailRouting';
import { getTravelApprovalPagedOptions } from './model/TravelApprovalQuery';
import { TravelApprovalFilter } from './model/TravelApprovalTypes';

export function TravelApprovalBrowse() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (!isTravelApproverRole(currentRole)) {
    return <NoContentPlaceholder title={t('travel.approvals.noAccess')} />;
  }

  return <TravelApprovalList />;
}

function TravelApprovalList() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const { checked: pendingOnly, onChange: setPendingOnly } = useBooleanFilter(true);
  const { pagination, useTotalElements, resetPagination, ...paginationProps } = usePagination({ search });
  // Pending requests are all SUBMITTED, so statuses only narrow the list once decided ones are included.
  const { filter: requestFilter, fields } = useTravelRequestFilters({
    initialYear: null,
    showStatuses: !pendingOnly,
    restrictDeanFaculty: false,
    onChange: resetPagination,
  });

  const filter: TravelApprovalFilter = {
    ...requestFilter,
    pendingOnly: pendingOnly ? null : false,
    // The research office's board step has its own list, so this one keeps the offices to their verification step.
    stepKeys: isTravelOfficeRole(currentRole) ? ['SECRETARIAT_VERIFICATION'] : [],
    sentToBoard: null,
  };

  const { data } = useSuspenseQuery(
    getTravelApprovalPagedOptions({
      ...pagination,
      ...filter,
      sortDirection,
      search,
    }),
  );
  useTotalElements(data);

  return (
    <Browse
      actions={currentRole === RoleType.FACULTY_ADMIN ? <TravelDeanMailRouting /> : null}
      title={t('travel.approvals.title')}
      pagination={paginationProps}
      filters={
        <FilterBox>
          <SearchFilter
            label={t('label.search')}
            description={t('travel.approvals.searchBy')}
            {...searchProps}
          />
          <BooleanFilter
            label={t('travel.approvals.filters.pendingOnly')}
            description={t('travel.approvals.filters.pendingOnlyDescription')}
            checked={pendingOnly}
            onChange={value => {
              setPendingOnly(value);
              resetPagination();
            }}
          />
          {fields}
        </FilterBox>
      }
      sortDirection={sortDirection}
      sortedBy={t('travel.approvals.sortedBy')}
      toggleSortDirection={toggleSortDirection}
      data={data.list}
      renderItem={request => (
        <TravelOverlapCardFrame
          key={request.id}
          requestId={request.id}
          overlapCount={request.overlapCount}
        >
          <TravelRequestCard
            request={request}
            to={`/travel/requests/${request.id}`}
            showOfficeStep
          />
        </TravelOverlapCardFrame>
      )}
    />
  );
}
